import {
  Conversation,
  ConversationMessageStatus,
  InlineDataConversationMessage,
  RichTextConversationMessage,
  ArticlesMessage as ArticlesMessageType,
} from '@stripe-internal/conversations-client';
import {FormattedMessage} from 'react-intl';
import {view, css} from '@sail/ui/deprecated';
import {messages} from './messages';
import {AvatarSpacer, TextRow} from '../../../Chat';
import {devLog} from '../../../helpers/dev';
import {ButtonOptionsMessage} from './ButtonOptionsMessage';
import {SelectOptionsMessage} from './SelectOptionsMessage';
import {InlineDataMessage} from './InlineDataMessage';
import {InlineDataMessageRow} from './InlineDataMessageRow';
import {ArticlesMessage} from './ArticlesMessage';
import {ArticlesMessageRow} from './ArticlesMessageRow';
import {ButtonsMessage} from './ButtonsMessage';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../../FeatureFlagsProvider';
import {TopicSelectorRowNext} from './TopicSelectorRowNext';

// isDotComConversation is duplicated here from conversations-assistant/support-conversations-client
// because conversations-ui cannot take a dependency on those packages (it is a lower-level package
// that they both depend on). Any changes to this logic must also be reflected in:
//   - lib/support_conversations/client/src/App/Screens/ChatScreen/conversationUtils.ts
//   - lib/support/web/frontend/conversations-assistant/src/core/lib/util/conversationUtils.ts
function isDotComConversation(conversation: Conversation): boolean {
  const ep = conversation.context.entryPoint;
  return ep === 'dot_com' || ep === 'dot_com_embedded_csf';
}

type Props = {
  conversation: Conversation;
  message: RichTextConversationMessage;
  avatar?: React.ReactNode | null | undefined;
  disableInteraction?: boolean;
};

export const RichTextMessageRow = ({
  conversation,
  message,
  avatar,
  disableInteraction,
}: Props) => {
  const useNewDesigns = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();

  // TODO(MSE-4736): revisit this and add a better pattern for this style of message
  if (
    useNewDesigns &&
    message.data.type === 'select-options-conversation-message' &&
    message.data.purpose === 'topic_selection'
  ) {
    return (
      <TopicSelectorRowNext conversation={conversation} message={message} />
    );
  }

  let embeddedMessage: React.ReactNode | null = null;
  let endMessage: React.ReactNode | null = null;
  switch (message.data.type) {
    case 'button-options-conversation-message': {
      embeddedMessage = (
        <ButtonOptionsMessage
          conversation={conversation}
          message={message.data}
          disableInteraction={disableInteraction}
        />
      );
      break;
    }
    case 'select-options-conversation-message': {
      embeddedMessage = (
        <SelectOptionsMessage
          conversation={conversation}
          message={message.data}
          disableInteraction={disableInteraction}
        />
      );
      break;
    }
    case 'articles-conversation-message': {
      if (message.data.articleStyle === 'sources_dropdown') {
        endMessage = (
          <ArticlesMessageRow
            conversation={conversation}
            avatar={<AvatarSpacer />}
            message={{...message, ...message.data} as ArticlesMessageType}
          />
        );
      } else {
        embeddedMessage = (
          <ArticlesMessage conversation={conversation} message={message.data} />
        );
      }
      break;
    }
    case 'buttons-conversation-message': {
      if (isDotComConversation(conversation)) {
        endMessage = (
          <ButtonsMessage
            conversation={conversation}
            message={message.data}
            messageId={message.id}
          />
        );
      } else {
        embeddedMessage = (
          <ButtonsMessage conversation={conversation} message={message.data} />
        );
      }
      break;
    }
    case 'inline-data-conversation-message': {
      if (message.data.inlineData.style === 'outer_bubble') {
        endMessage = (
          <InlineDataMessageRow
            conversation={conversation}
            avatar={<AvatarSpacer />}
            message={
              {...message, ...message.data} as InlineDataConversationMessage
            }
          />
        );
      } else {
        embeddedMessage = (
          <InlineDataMessage
            conversation={conversation}
            message={message.data}
          />
        );
      }
      break;
    }
    default: {
      devLog(
        'RichTextMessage: unknown message type',
        (message.data as any).type,
      );
    }
  }

  const background =
    message.participant.id === conversation.user.id
      ? 'neutral600'
      : 'neutral100';

  const position =
    message.participant.id === conversation.user.id ? 'end' : 'start';

  // In the future we probably want to show borders around more types to be
  // consistent, but start small to reduce the risk
  const showBorder =
    useNewDesigns &&
    message.data.type === 'select-options-conversation-message';

  return (
    <view.div
      data-testid="rich-text-message-row"
      css={{
        stack: 'y',
        gap: 'small',

        ...(showBorder
          ? {
              background: 'surface',
              padding: 'medium',
              borderRadius: css.value('12px'),
              border: css.value('1px solid'),
              borderColor: 'neutral',
            }
          : {}),
      }}
    >
      <TextRow
        messageId={message.id}
        useDotComDesign={useDotComDesign}
        body={message.text}
        avatar={avatar}
        position={position}
        background={background}
        messageType={message.participant.role}
        error={
          message.status === ConversationMessageStatus.Error ? (
            <FormattedMessage {...messages.sendError} />
          ) : null
        }
      >
        {embeddedMessage}
      </TextRow>
      {endMessage}
    </view.div>
  );
};
